import { baseApi } from './baseApi';

// Device shape returned by the DPS enrollment listing endpoints
export interface DpsEnrollmentDevice {
  id: string;
  device_id: string;
  registration_id?: string;
  name?: string;
  customer_name?: string;
  status?: string;
  dps_enrollment_status?: string;
  dps_assigned_hub?: string;
  dps_enrolled_at?: string;
  dps_last_error?: string;
  dps_id_scope?: string;
  dps_environment?: string;
  has_tpm?: boolean;
}

export interface DpsEnrollmentListResponse {
  data: DpsEnrollmentDevice[];
}

export interface BulkDpsEnrollRequest {
  connection_string: string;
  device_ids: string[];
}

export interface DpsEnrollmentResult {
  device_id: string;
  success?: boolean;
  status?: string;
  error?: string;
  registration_id?: string;
  assigned_hub?: string;
}

export interface BulkDpsEnrollResponse {
  devices: DpsEnrollmentResult[];
  total: number;
  successful: number;
  failed: number;
  skipped: number;
}

export interface DpsEnrollmentJsonExport {
  export_metadata?: {
    total_devices?: number;
    error_count?: number;
  };
  [key: string]: unknown;
}

// Extend the base API with devices endpoints
export const devicesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // List devices for DPS enrollment. When `unenrolled` is true, fetch only
    // devices not yet enrolled; otherwise fetch all devices (client filters).
    getDpsEnrollments: builder.query<DpsEnrollmentListResponse, { unenrolled: boolean }>({
      query: ({ unenrolled }) => ({
        url: unenrolled ? '/dps/devices/unenrolled' : '/devices/',
        params: unenrolled ? {} : { page_size: 10000 },
      }),
      providesTags: [{ type: 'Device', id: 'LIST' }],
    }),

    // Bulk-enroll selected devices in Azure DPS. The target DPS - and with it
    // the ID Scope recorded against each device - comes from the connection
    // string's host, which the server resolves.
    bulkDpsEnroll: builder.mutation<BulkDpsEnrollResponse, BulkDpsEnrollRequest>({
      query: (body) => ({
        url: '/dps/enroll-bulk',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Device', id: 'LIST' }],
    }),

    // Export DPS enrollment data as CSV
    exportDpsEnrollmentCsv: builder.mutation<Blob, { deviceIds?: string[] }>({
      query: ({ deviceIds }) => ({
        url: '/devices/dps-enrollment/export/csv',
        method: 'GET',
        params: deviceIds?.length ? { device_ids: deviceIds.join(',') } : {},
        responseHandler: (response) => response.blob(),
      }),
    }),

    // Export DPS enrollment data as JSON
    exportDpsEnrollmentJson: builder.mutation<DpsEnrollmentJsonExport, { deviceIds?: string[] }>({
      query: ({ deviceIds }) => ({
        url: '/devices/dps-enrollment/export/json',
        method: 'GET',
        params: deviceIds?.length ? { device_ids: deviceIds.join(',') } : {},
      }),
    }),
  }),
});

export const {
  useGetDpsEnrollmentsQuery,
  useBulkDpsEnrollMutation,
  useExportDpsEnrollmentCsvMutation,
  useExportDpsEnrollmentJsonMutation,
} = devicesApi;
